import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { InfoIcon, X, Info, Star, ChevronRight } from "lucide-react";
import history from "utils/history";
import storage from "utils/Storage";
import { getUID } from 'utils/Helper';
import { useMyProfileQuery, useLevelQuery } from "hooks/useUser";
import "./VipClub.css";

// ─── Styled Components from original code ─────────────────────────────────────
const Container = styled.div`
  color: white;
  padding: 20px;
`;

const LevelsScrollContainer = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 12px;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const CarouselContainerVip = styled.div`
  margin-right: 170px;
  @media (max-width: 454px) {
    margin-right: 90px !important;
  }
  @media (max-width: 284px) {
    margin-right: 50px !important;
  }
`;

const CardVip = styled.div`
  color: white;
  border-radius: 10px;
  padding: 20px;
  width: 100%;
  position: relative;
  cursor: pointer;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 150px;
  padding: 10px;

  @media (max-width: 854px) {
    display: flex;
    justify-content: space-around;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #1E2D35;
  border-radius: 4px;
  overflow: hidden;
`;

const Progress = styled.div`
  width: ${(props) => props.progress}%;
  height: 100%;
  background-color: #5865f2;
  border-radius: 4px;
`;

const LevelIndicator = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 8px;
`;

const LevelCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: clamp(14px, 2vw, 18px);
  margin-right: 8px;
  text-transform: no-wrap;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  color: #72767d;
  font-size: 12px;
`;

const MainWrapper = styled.div`
  display: flex;
  height: 180px;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
  position: relative;
`;

const InfoContainer = styled.div`
  flex: 1;
`;

const InfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const CustomModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #17181b;
  color: white;
  width: 900px;
  border-radius: 8px;
  padding: 20px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.div`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const ModalBody = styled.div`
  max-height: 80vh;
  overflow-y: auto;
  text-align: center;
`;

// ─── Tier theme config ────────────────────────────────────────────────────────
const tierTheme = {
  bronze: {
    gradient: "linear-gradient(135deg, #3d2b1f 0%, #1a1008 100%)",
    border: "#cd7f3240",
    glow: "rgba(205, 127, 50, 0.15)",
    text: "#cd7f32",
    badge: "/assets/bcimages/vipclub/badge-bronze.webp",
    bgCard: "rgba(205,127,50,0.08)",
  },
  silver: {
    gradient: "linear-gradient(135deg, #2a2f35 0%, #0f1117 100%)",
    border: "#c0c0c040",
    glow: "rgba(192, 192, 192, 0.15)",
    text: "#c0c0c0",
    badge: "/assets/bcimages/vipclub/badge-silver.webp",
    bgCard: "rgba(192,192,192,0.08)",
  },
  gold: {
    gradient: "linear-gradient(135deg, #3d3000 0%, #1a1200 100%)",
    border: "#ffd70040",
    glow: "rgba(255, 215, 0, 0.15)",
    text: "#ffd700",
    badge: "/assets/bcimages/vipclub/badge-gold.webp",
    bgCard: "rgba(255,215,0,0.08)",
  },
  platinum: {
    gradient: "linear-gradient(135deg, #2a1f3d 0%, #100a1a 100%)",
    border: "#9370db40",
    glow: "rgba(147, 112, 219, 0.18)",
    text: "#9370db",
    badge: "/assets/bcimages/vipclub/badge-platinum.webp",
    bgCard: "rgba(147,112,219,0.08)",
  },
  diamond: {
    gradient: "linear-gradient(135deg, #2f1a2b 0%, #120811 100%)",
    border: "#dc8abb40",
    glow: "rgba(220, 138, 187, 0.18)",
    text: "#dc8abb",
    badge: "/assets/bcimages/vipclub/badge-none.webp",
    bgCard: "rgba(220,138,187,0.08)",
  },
};

const getTierTheme = (tier) => tierTheme[tier] || tierTheme.bronze;

// ─── VIP Level Card ───────────────────────────────────────────────────────────
const VipLevelCard = ({ level, isCurrentLevel, isUnlocked }) => {
  const theme = getTierTheme(level.tier);
  return (
    <div
      className="vip-level-card"
      style={{
        background: isCurrentLevel ? theme.gradient : "rgba(255,255,255,0.02)",
        border: `1.5px solid ${isCurrentLevel ? theme.border.replace("40", "90") : "rgba(255,255,255,0.06)"}`,
        borderRadius: 16,
        padding: "20px 16px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        cursor: "default",
        flexShrink: 0,
        width: 180,
        boxShadow: isCurrentLevel ? `0 0 30px ${theme.glow}` : "none",
      }}
    >
      {isCurrentLevel && (
        <div style={{
          position: "absolute", top: 8, right: 8,
          background: theme.text + "22", border: `1px solid ${theme.text}55`,
          color: theme.text, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, textTransform: "uppercase", letterSpacing: 1
        }}>
          Current
        </div>
      )}

      {/* Badge icon */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: theme.bgCard,
          border: `2px solid ${theme.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto", fontSize: 22, fontWeight: 900, color: theme.text,
          boxShadow: isCurrentLevel ? `0 0 15px ${theme.glow}` : "none",
        }}>
          {level.level_number}
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{level.level_name}</div>
        <div style={{ color: theme.text, fontSize: 11, fontWeight: 600, textTransform: "capitalize", marginBottom: 10 }}>{level.tier}</div>

        {/* Unlock condition */}
        <div style={{
          background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 10px", marginBottom: 10,
          fontSize: 11, color: "rgba(255,255,255,0.5)"
        }}>
          {level.condition_type === 'both' ? (
            <>
              <div>
                <span style={{ color: "rgba(255,255,255,0.4)" }}>Bet: </span>
                <span style={{ color: theme.text, fontWeight: 700 }}>${Number(level.required_amount).toLocaleString()}</span>
              </div>
              <div style={{ marginTop: 2 }}>
                <span style={{ color: "rgba(255,255,255,0.4)" }}>Dep: </span>
                <span style={{ color: theme.text, fontWeight: 700 }}>${Number(level.required_deposit || 0).toLocaleString()}</span>
              </div>
            </>
          ) : level.condition_type === 'deposit' ? (
            <>
              <span style={{ color: theme.text, fontWeight: 700 }}>${Number(level.required_deposit || 0).toLocaleString()}</span>
              <br />
              <span>Total Deposit</span>
            </>
          ) : (
            <>
              <span style={{ color: theme.text, fontWeight: 700 }}>${Number(level.required_amount).toLocaleString()}</span>
              <br />
              <span>Total Bet</span>
            </>
          )}
        </div>

        {/* Perks */}
        <div style={{ textAlign: "left" }}>
          {(level.perks || []).slice(0, 4).map((perk, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
              <span style={{ color: theme.text, fontSize: 10 }}>✓</span>
              <span style={{ color: isUnlocked ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)", fontSize: 11 }}>{perk}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Rotating Card Data ──────────────────────────────────────────────────────
const cardData = [
  {
    title: "Bronze Card",
    description: "Smart Player",
    subtitle: "You deserve the best",
    imageSrc: "/assets/images/vip-new/vip-1.webp",
  },
  {
    title: "Silver Card",
    description: "VIP Player",
    subtitle: "Enjoy exclusive benefits",
    imageSrc: "/assets/images/vip-new/vip-3.webp",
  },
  {
    title: "Gold Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-4.png",
  },
  {
    title: "Platinum Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-5.webp",
  },
  {
    title: "Diamond Card",
    description: "Elite Player",
    subtitle: "Experience luxury gaming",
    imageSrc: "/assets/images/vip-new/vip-2.webp",
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const VipClub = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressAnimationRef = useRef(null);

  const [userInfo, setUserInfo] = useState({});
  const [levelList, setLevelList] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [nextLevelAmount, setNextLevelAmount] = useState(0);
  const [currentLevelAmount, setCurrentLevelAmount] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [wagerProgressPercentage, setWagerProgressPercentage] = useState(0);
  const [depositProgressPercentage, setDepositProgressPercentage] = useState(0);

  const [uid] = useState(getUID);

  const { data: myProfileData } = useMyProfileQuery(uid);
  const { data: levelData } = useLevelQuery();

  useEffect(() => {
    if (myProfileData?.data) {
      setUserInfo(myProfileData.data[0]);
    }
  }, [myProfileData]);

  useEffect(() => {
    if (levelData?.data) {
      setLevelList(levelData.data);
    }
  }, [levelData]);

  const animateProgress = (targetProgress) => {
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
    }
    let currentProgress = 0;
    const duration = 1000; // 1 second animation
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progressVal = Math.min(elapsed / duration, 1);

      currentProgress = progressVal * targetProgress;
      setProgress(currentProgress);

      if (progressVal < 1) {
        progressAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    progressAnimationRef.current = requestAnimationFrame(animate);
  };

  function calculateLevelPercentage() {
    if (!userInfo || !levelList?.length) return;

    let myTotalBet = parseFloat(userInfo.total_bet_amount || 0);
    let myDeposit = parseFloat(userInfo.total_deposit_amount || 0);
    let curLvl = 0;
    let curRequiredBet = 0;
    let curRequiredDep = 0;
    let nextLvlObj = null;

    for (let i = 0; i < levelList.length; i++) {
      const lvl = levelList[i];
      let isMet = false;
      const reqBet = parseFloat(lvl.required_amount || 0);
      const reqDep = parseFloat(lvl.required_deposit || 0);

      if (lvl.condition_type === 'deposit') {
        isMet = myDeposit >= reqDep;
      } else if (lvl.condition_type === 'both') {
        isMet = myTotalBet >= reqBet && myDeposit >= reqDep;
      } else {
        isMet = myTotalBet >= reqBet;
      }

      if (isMet) {
        curLvl = lvl.level_number;
        curRequiredBet = reqBet;
        curRequiredDep = reqDep;
      } else {
        nextLvlObj = lvl;
        break;
      }
    }

    let percentage = 0;
    let betPct = 0;
    let depPct = 0;

    if (nextLvlObj) {
      const nextRequiredBet = parseFloat(nextLvlObj.required_amount || 0);
      const nextRequiredDep = parseFloat(nextLvlObj.required_deposit || 0);
      const condition = nextLvlObj.condition_type || 'bet';

      betPct = nextRequiredBet > 0 ? Math.min(Math.max((myTotalBet / nextRequiredBet) * 100, 0), 100) : 100;
      depPct = nextRequiredDep > 0 ? Math.min(Math.max((myDeposit / nextRequiredDep) * 100, 0), 100) : 100;

      if (condition === 'deposit') {
        percentage = depPct;
      } else if (condition === 'both') {
        percentage = Math.min(betPct, depPct);
      } else {
        percentage = betPct;
      }

      setNextLevelAmount(condition === 'deposit' ? nextRequiredDep : nextRequiredBet);
    } else {
      percentage = 100;
      betPct = 100;
      depPct = 100;
      setNextLevelAmount(0);
    }

    setCurrentLevel(curLvl);
    setCurrentLevelAmount(nextLvlObj ? (nextLvlObj.condition_type === 'deposit' ? curRequiredDep : curRequiredBet) : curRequiredBet);
    setProgressPercentage(percentage);
    setWagerProgressPercentage(betPct);
    setDepositProgressPercentage(depPct);

    animateProgress(Math.min(Math.max(percentage, 0), 100));
  }

  useEffect(() => {
    calculateLevelPercentage();
  }, [userInfo, levelList]);

  useEffect(() => {
    return () => {
      if (progressAnimationRef.current) {
        cancelAnimationFrame(progressAnimationRef.current);
      }
    };
  }, []);

  // Update active rotating card based on current tier
  useEffect(() => {
    if (levelList?.length && currentLevel > 0) {
      const curLvlObj = levelList.find(l => l.level_number === currentLevel);
      if (curLvlObj) {
        const tier = curLvlObj.tier?.toLowerCase();
        if (tier === 'bronze') setActiveIndex(0);
        else if (tier === 'silver') setActiveIndex(1);
        else if (tier === 'gold') setActiveIndex(2);
        else if (tier === 'platinum') setActiveIndex(3);
        else if (tier === 'diamond') setActiveIndex(4);
      }
    }
  }, [currentLevel, levelList]);

  const handleCardClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const activeCard = cardData[activeIndex];
  const activeLevels = levelList.filter(l => l.is_active !== false);
  const isLoggedIn = storage.getKey('logged') !== null;

  return (
    <div className="casino-container" style={{ color: "white", marginTop: "24px" }}>
      <style>{`
        @media (max-width: 991px) {
          .vip-stars-container {
            display: none !important;
          }
        }
        .vip-progress-hero {
          padding: 24px 32px;
        }
        @media (max-width: 768px) {
          .vip-progress-hero {
            padding: 16px 16px !important;
          }
        }
      `}</style>
      {/* ── VIP PROGRESS HERO (Top Section) ── */}
      {isLoggedIn ? (
        <section className="vip-progress-hero" style={{
          backgroundColor: "#1a2c38",
          borderRadius: "8px",
          position: "relative",
          overflow: "hidden",
          minHeight: "220px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          border: "1px solid rgba(255,255,255,0.04)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "white", margin: 0 }}>Your VIP Progress</h2>
            <div 
              style={{ position: "relative", display: "inline-block" }}
              onMouseEnter={() => setShowInfoTooltip(true)}
              onMouseLeave={() => setShowInfoTooltip(false)}
            >
              <button
                style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "#b1bad3" }}
                aria-label="VIP info"
              >
                <Info size={16} />
              </button>
              {showInfoTooltip && (
                <div 
                  className="absolute left-[-110px] sm:left-0 top-6 z-20 w-[280px] sm:w-[320px] max-w-[calc(100vw-40px)] rounded-md bg-[#0f212e] border border-gray-800 shadow-xl p-3 text-[13px] font-normal text-gray-300 leading-relaxed animate-in fade-in zoom-in-95 duration-100"
                  style={{ border: "1px solid rgba(255,255,255,0.05)", textAlign: "left" }}
                >
                  <div 
                    className="absolute -top-1.5 left-[118px] sm:left-2 w-3 h-3 rotate-45 bg-[#0f212e]" 
                    style={{ borderLeft: "1px solid rgba(255,255,255,0.05)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                  />
                  {window?.siteConfig?.vip_tooltip_text || `All settled sports and racing bets contribute progress at 3x. Casino bets contribute progress at 1x, except Only on SevenX games, which contribute progress at 2x. Voided bets do not count.`}
                </div>
              )}
            </div>
          </div>
          <div style={{
            width: "100%",
            maxWidth: "500px",
            borderRadius: "8px",
            border: "1px solid #2f4553",
            padding: "20px",
            backgroundColor: "#122431"
          }}>
            {/* Header row: Username on left, Next level on right */}
            <div style={{ display: "flex", alignItems: "center", justifySpaceBetween: "space-between", justifyContent: "space-between", width: "100%", marginBottom: "12px" }}>
              <button style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "white",
                fontWeight: "bold",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontSize: "14px"
              }}>
                {(() => {
                  let pName = "Player";
                  if (userInfo) {
                    let prof = userInfo.profile;
                    if (typeof prof === 'string') {
                      try { prof = JSON.parse(prof); } catch(e) {}
                    }
                    pName = prof?.name || userInfo.name || userInfo.username || userInfo.email || "Player";
                  }
                  return pName.split('@')[0];
                })()} <ChevronRight size={16} style={{ color: "#559cfc" }} />
              </button>
              
              <span style={{ fontSize: '12px', color: '#b1bad3' }}>
                Next level: <strong style={{ color: '#fff' }}>{levelList.find(l => l.level_number === currentLevel + 1)?.level_name || "Bronze"}</strong>
              </span>
            </div>
 
            {/* Progress Blocks */}
            {(() => {
              const nextLvlObj = levelList.find(l => l.level_number === currentLevel + 1);
              const condition = nextLvlObj?.condition_type || 'bet';
              
              const formatUSD = (val) => {
                const num = Number(val) || 0;
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: num % 1 === 0 ? 0 : 2,
                  maximumFractionDigits: 2
                }).format(num);
              };
              
              const nextRequiredBet = parseFloat(nextLvlObj?.required_amount || 0);
              const nextRequiredDep = parseFloat(nextLvlObj?.required_deposit || 0);
              const totalWageredUsd = parseFloat(userInfo?.total_bet_amount || 0);
              const totalDepositUsd = parseFloat(userInfo?.total_deposit_amount || 0);
 
              if (condition === 'both') {
                return (
                  <div style={{ marginTop: '14px', width: '100%' }}>
                    {/* Side-by-side Progress Bars Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      {/* Wager Block */}
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 12px', borderRadius: '6px', border: '1px solid #2f4553' }}>
                        <div style={{ display: 'flex', justifySpaceBetween: "space-between", justifyContent: "space-between", alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', color: '#b1bad3', fontWeight: 'bold', textTransform: 'uppercase' }}>Wager</span>
                          <span style={{ fontSize: '11px', color: '#25de82', fontWeight: 'bold' }}>{wagerProgressPercentage.toFixed(1)}%</span>
                        </div>
                        <ProgressBar style={{ height: '4px' }}>
                          <Progress progress={wagerProgressPercentage} style={{ backgroundColor: '#25de82' }} />
                        </ProgressBar>
                        <div style={{ fontSize: '10px', color: '#25de82', marginTop: '6px', fontWeight: '600' }}>
                          {formatUSD(totalWageredUsd)} / {formatUSD(nextRequiredBet)}
                        </div>
                      </div>
 
                      {/* Deposit Block */}
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 12px', borderRadius: '6px', border: '1px solid #2f4553' }}>
                        <div style={{ display: 'flex', justifySpaceBetween: "space-between", justifyContent: "space-between", alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', color: '#b1bad3', fontWeight: 'bold', textTransform: 'uppercase' }}>Deposit</span>
                          <span style={{ fontSize: '11px', color: '#2196f3', fontWeight: 'bold' }}>{depositProgressPercentage.toFixed(1)}%</span>
                        </div>
                        <ProgressBar style={{ height: '4px' }}>
                          <Progress progress={depositProgressPercentage} style={{ backgroundColor: '#2196f3' }} />
                        </ProgressBar>
                        <div style={{ fontSize: '10px', color: '#2196f3', marginTop: '6px', fontWeight: '600' }}>
                          {formatUSD(totalDepositUsd)} / {formatUSD(nextRequiredDep)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
 
              // Single progress bar flow
              const isDeposit = condition === 'deposit';
              const currentVal = isDeposit ? totalDepositUsd : totalWageredUsd;
              const to = isDeposit ? nextRequiredDep : nextRequiredBet;
              const pct = isDeposit ? depositProgressPercentage : wagerProgressPercentage;
 
              return (
                <div style={{ width: '100%', marginTop: '12px' }}>
                  <div 
                    className="flex items-center gap-2 text-sm text-white font-bold relative"
                    style={{ width: "fit-content", display: "flex", gap: "6px" }}
                  >
                    <span>{pct.toFixed(2)}%</span>
                  </div>
                  <ProgressBar style={{ height: '6px', marginTop: '8px' }}>
                    <Progress progress={pct} style={{ backgroundColor: isDeposit ? '#2196f3' : '#25de82' }} />
                  </ProgressBar>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', fontSize: '12px', color: '#b1bad3' }}>
                    <span>{isDeposit ? 'Dep: ' : 'Wager: '}{formatUSD(currentVal)} / {formatUSD(to)}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right Stars design */}
          <div style={{
            position: "absolute",
            right: "48px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "flex-end",
            gap: "12px"
          }} className="vip-stars-container">
            <Star size={56} style={{ color: "oklch(0.78 0.16 70)" }} fill="currentColor" />
            <div style={{
              width: "140px",
              height: "140px",
              borderRadius: "12px",
              border: "2px solid rgba(255,255,255,0.08)",
              display: "grid",
              placeItems: "center",
              background: "oklch(0.22 0.02 240 / .6)"
            }}>
              <Star size={90} style={{ color: "oklch(0.78 0.16 70)" }} fill="currentColor" />
            </div>
            <Star size={56} className="opacity-40" style={{ color: "oklch(0.5 0.05 250)" }} />
          </div>
        </section>
      ) : (
        /* Logged-out Hero */
        <section style={{
          backgroundColor: "#1a2c38",
          borderRadius: "8px",
          padding: "32px 40px",
          position: "relative",
          overflow: "hidden",
          minHeight: "220px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          border: "1px solid rgba(255,255,255,0.04)"
        }}>
          <h1 style={{ fontSize: "clamp(20px, 4vw, 32px)", fontWeight: 800, color: "white", maxWidth: "60%", margin: 0 }}>
            {window?.siteConfig?.vip_page_heading || "The unrivalled VIP experience"}
          </h1>
          <p style={{ marginTop: "12px", color: "#b1bad3", fontSize: "14px", maxWidth: "60%", lineHeight: 1.5 }}>
            {window?.siteConfig?.vip_page_subtitle || "Unlock exclusive benefits and receive instantly withdrawable bonuses without any strings attached"}
          </p>
          <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
            <button 
              onClick={() => history.push('/register')} 
              style={{
                padding: "0 24px",
                height: "40px",
                borderRadius: "4px",
                backgroundColor: "#1475e1",
                color: "white",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "#1d7fe8"}
              onMouseOut={(e) => e.target.style.backgroundColor = "#1475e1"}
            >
              Register
            </button>
            <button 
              onClick={() => history.push('/login')} 
              style={{
                padding: "0 24px",
                height: "40px",
                borderRadius: "4px",
                backgroundColor: "#2f4553",
                color: "white",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = "#3d5668"}
              onMouseOut={(e) => e.target.style.backgroundColor = "#2f4553"}
            >
              Login
            </button>
          </div>
          <div style={{
            position: "absolute",
            right: "48px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }} className="hidden md:flex">
            <Star size={48} style={{ color: "oklch(0.78 0.16 70)" }} fill="currentColor" />
            <Star size={96} style={{ color: "oklch(0.4 0.05 250)" }} fill="currentColor" />
            <Star size={48} className="opacity-40" style={{ color: "oklch(0.5 0.05 250)" }} />
          </div>
        </section>
      )}

      {/* ── VIP RANKING SYSTEM (Middle Section) ── */}
      <section style={{ marginTop: "48px" }}>
        <h2 style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", color: "white", marginBottom: "24px" }}>VIP ranking system</h2>
        <div style={{ overflowX: "auto", paddingBottom: "8px" }} className="vip-scroll">
          <div style={{ display: "flex", gap: "12px", minWidth: "max-content", alignItems: "stretch" }}>
            {activeLevels.map((lvl, i) => {
              const c = lvl.badge_color || ["#a37148", "#b8b8c4", "#d6a040", "#5fc3d4", "#5fb0d4", "#5fb0d4", "#5fb0d4"][i] || "#5fc3d4";
              let isUnlocked = false;
              if (lvl.condition_type === 'deposit') {
                isUnlocked = parseFloat(userInfo?.total_deposit_amount || 0) >= parseFloat(lvl.required_deposit || 0);
              } else if (lvl.condition_type === 'both') {
                isUnlocked = parseFloat(userInfo?.total_bet_amount || 0) >= parseFloat(lvl.required_amount) && parseFloat(userInfo?.total_deposit_amount || 0) >= parseFloat(lvl.required_deposit || 0);
              } else {
                isUnlocked = parseFloat(userInfo?.total_bet_amount || 0) >= parseFloat(lvl.required_amount);
              }
              
              return (
                <div key={lvl.id} style={{ width: "220px", display: "flex", flexDirection: "column", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                    <div style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      border: `2px solid ${c}`,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      backgroundColor: isUnlocked ? c : "transparent"
                    }}>
                      <Star className="w-5 h-5" style={{ color: isUnlocked ? "#1a2c38" : c }} fill="currentColor" />
                    </div>
                    {i < activeLevels.length - 1 && (
                      <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.08)", marginLeft: "16px", marginRight: "-16px" }} />
                    )}
                  </div>
                  <div style={{ flex: 1, display: "flex" }}>
                    <div style={{
                      backgroundColor: "#1a2c38",
                      borderRadius: "8px",
                      padding: "20px",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: `1px solid ${isUnlocked ? 'rgba(37, 222, 130, 0.15)' : '#2f4553'}`,
                      boxShadow: isUnlocked ? '0 0 15px rgba(37, 222, 130, 0.05)' : 'none'
                    }}>
                      <span style={{
                        alignSelf: "flex-start",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: `${c}22`,
                        color: c
                      }}>
                        {lvl.level_name}
                      </span>
                      <div style={{ marginTop: "16px", fontSize: "24px", fontWeight: 800, color: "white" }}>
                        {lvl.condition_type === 'both' ? (
                          <>
                            <div style={{ fontSize: "16px", fontWeight: 700 }}>
                              <span style={{ opacity: 0.5, fontSize: "12px", fontWeight: 500 }}>Bet: </span>
                              ${Number(lvl.required_amount).toLocaleString()}
                            </div>
                            <div style={{ fontSize: "16px", fontWeight: 700, marginTop: "4px" }}>
                              <span style={{ opacity: 0.5, fontSize: "12px", fontWeight: 500 }}>Dep: </span>
                              ${Number(lvl.required_deposit).toLocaleString()}
                            </div>
                          </>
                        ) : lvl.condition_type === 'deposit' ? (
                          `$${Number(lvl.required_deposit).toLocaleString()}`
                        ) : (
                          `$${Number(lvl.required_amount).toLocaleString()}`
                        )}
                      </div>
                      <div style={{ fontSize: "12px", color: "#b1bad3", marginTop: "4px" }}>
                        {lvl.condition_type === 'both' ? 'Both conditions' : lvl.condition_type === 'deposit' ? 'Deposit amount' : 'Wager amount'}
                      </div>
                      <ul style={{ marginTop: "20px", listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                        {(lvl.perks || []).slice(0, 5).map((p, pIdx) => (
                          <li key={pIdx} style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "white", fontSize: "13px" }}>
                            <span style={{ color: "#25de82", fontWeight: "bold" }}>✓</span>
                            <span style={{ color: isUnlocked ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.4)" }}>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {showModal && (
        <CustomModal onClick={handleCloseModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{activeCard?.title || "About VIP Cards"}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <img
                src={activeCard?.imageSrc || "/assets/images/vip-new/vip-1.webp"}
                style={{
                  height: "170px",
                  width: "292px",
                  objectFit: "contain",
                  marginBottom: "16px"
                }}
                alt="VIP Card"
              />
              <h3>{activeCard?.description}</h3>
              <p style={{ color: "rgba(255,255,255,0.6)", marginTop: "8px" }}>{activeCard?.subtitle}</p>
            </ModalBody>
          </ModalContent>
        </CustomModal>
      )}
    </div>
  );
};

export default VipClub;
